import { useId } from 'react';
import { Capacitor } from '@capacitor/core';
import { Link } from 'react-router';
import type { PolicyConfirmationsValue } from '../services/authService';

interface PolicyConfirmationsProps {
  value: PolicyConfirmationsValue;
  onChange: (value: PolicyConfirmationsValue) => void;
  disabled?: boolean;
}

export default function PolicyConfirmations({
  value,
  onChange,
  disabled = false,
}: PolicyConfirmationsProps) {
  const id = useId();
  const adultId = `${id}-adult`;
  const termsId = `${id}-terms`;
  // Native WebViews do not reliably open target="_blank" windows. Keep legal
  // documents in the app, while retaining a separate tab for browser signup.
  const legalTarget = Capacitor.isNativePlatform() ? undefined : '_blank';

  return (
    <fieldset
      disabled={disabled}
      className="rounded-2xl border p-4 flex flex-col gap-4"
      style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}
    >
      <legend className="sr-only">Age and policy confirmations</legend>

      <div className="flex items-start gap-3">
        <input
          id={adultId}
          type="checkbox"
          checked={value.adultAttested}
          onChange={(event) => onChange({
            ...value,
            adultAttested: event.target.checked,
          })}
          className="mt-0.5 size-5 shrink-0 accent-[var(--app-accent-solid)]"
        />
        <label htmlFor={adultId} className="text-sm leading-5" style={{ color: 'var(--app-text)' }}>
          I confirm that I am 18 years of age or older.
        </label>
      </div>

      <div className="flex items-start gap-3">
        <input
          id={termsId}
          type="checkbox"
          checked={value.termsAccepted}
          onChange={(event) => onChange({
            ...value,
            termsAccepted: event.target.checked,
          })}
          className="mt-0.5 size-5 shrink-0 accent-[var(--app-accent-solid)]"
        />
        <label htmlFor={termsId} className="text-sm leading-5" style={{ color: 'var(--app-text)' }}>
          I have read and agree to the{' '}
          <Link
            to="/terms"
            target={legalTarget}
            rel="noopener noreferrer"
            className="underline"
            style={{ color: 'var(--app-accent)' }}
          >
            Terms of Service
          </Link>
          .
        </label>
      </div>

      <p className="text-xs leading-5 pl-8" style={{ color: 'var(--app-text-muted)' }}>
        Please also review our{' '}
        <Link
          to="/privacy"
          target={legalTarget}
          rel="noopener noreferrer"
          className="underline"
          style={{ color: 'var(--app-accent)' }}
        >
          Privacy Policy
        </Link>
        . It is a notice, not a separate consent checkbox.
      </p>
    </fieldset>
  );
}
